import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { MotionConfig, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Shirt, Swords, Users, Instagram, Twitter } from "lucide-react";
import api from "@/lib/api";
import MatchCard from "@/components/MatchCard";
import NewsCard from "@/components/NewsCard";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import FanWall from "@/components/FanWall";
import NewsletterSection from "@/components/NewsletterSection";
import PartnerStrip from "@/components/PartnerStrip";
import DiscordIcon from "@/components/DiscordIcon";
import CommunityPoll from "@/components/CommunityPoll";
import { useUiTexts } from "@/lib/uiTexts";
import Countdown from "@/components/Countdown";
import Streamers from "@/components/Streamers";
import CountUp from "@/components/CountUp";
import WordsReveal from "@/components/WordsReveal";

const HERO_IMG = "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=2000&auto=format&fit=crop";
const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, index, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.25 + index * 0.15, ease: EASE }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const CHAPTERS = [
  { num: "01", title: "La Compétition", text: "Nos rosters LoL et TFT s'entraînent chaque semaine pour arracher les qualifications — Nexus Tour, Open Tour, LFL. Chaque match est une bataille, chaque victoire vous appartient.", link: "/resultats", linkLabel: "Suivre les résultats" },
  { num: "02", title: "La Communauté", text: "Emisphere, c'est d'abord vous. Résultats en direct, coulisses, annonces : restez au plus près de l'équipe et faites grandir la structure avec nous.", link: "/actualites", linkLabel: "Lire les actus" },
  { num: "03", title: "Le Maillot", text: "Porter le maillot Emisphere, c'est porter la Faille. Éditions limitées, flocage à votre pseudo, stocks comptés — chaque commande finance directement la structure.", link: "/boutique", linkLabel: "Voir la boutique" },
];

// Bande orange en biais entre deux sections
const SectionDivider = () => (
  <div aria-hidden className="relative h-12 overflow-hidden">
    <div className="absolute inset-x-0 top-1/2 h-1 -skew-y-1 bg-gradient-to-r from-transparent via-orange-500/70 to-transparent" />
    <div className="absolute inset-x-0 top-1/2 mt-2 h-px -skew-y-1 bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />
  </div>
);

const DEFAULT_ORDER = ["marquee", "results", "poll", "manifesto", "fanwall", "news", "partners", "newsletter", "banner"];

export default function Home() {
  const [matches, setMatches] = useState([]);
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState([]);
  const [heroImg, setHeroImg] = useState(HERO_IMG);
  const [texts, setTexts] = useState({});
  const [marquee, setMarquee] = useState({ text: "", color: "#FF5E00" });
  const [chapters, setChapters] = useState(CHAPTERS);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [discordUrl, setDiscordUrl] = useState("");
  const [discordOnline, setDiscordOnline] = useState(null);
  const ui = useUiTexts();
  const { scrollY, scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  // Lueur orange qui suit doucement la souris dans le haut de page
  const glowX = useSpring(useMotionValue(-400), { stiffness: 60, damping: 20 });
  const glowY = useSpring(useMotionValue(-400), { stiffness: 60, damping: 20 });
  const moveGlow = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    glowX.set(e.clientX - r.left - 300);
    glowY.set(e.clientY - r.top - 300);
  };
  const bgY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroFade = useTransform(scrollY, [0, 550], [1, 0.3]);

  useEffect(() => {
    api.get("/matches").then((r) => setMatches(r.data.slice(0, 3))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data.items || [])).catch(() => {});
    api.get("/theme").then((r) => { if (r.data.hero_image) setHeroImg(r.data.hero_image); }).catch(() => {});
    api.get("/home-texts").then((r) => setTexts(r.data)).catch(() => {});
    api.get("/marquee").then((r) => setMarquee(r.data)).catch(() => {});
    api.get("/chapters").then((r) => { if (r.data.items?.length) setChapters(r.data.items); }).catch(() => {});
    api.get("/home-layout").then((r) => {
      if (r.data.order?.length) setOrder(r.data.order);
      setHiddenSections(r.data.hidden || []);
    }).catch(() => {});
    api.get("/social/followers").then((r) => setFollowers(r.data.data || [])).catch(() => {});
    api.get("/socials").then((r) => setDiscordUrl(r.data.discord || "")).catch(() => {});
    api.get("/discord/online").then((r) => { if (r.data?.online != null) setDiscordOnline(r.data.online); }).catch(() => {});
  }, []);


  const sections = {
    marquee: <Marquee items={(marquee.text || "").split(" /// ").filter(Boolean)} color={marquee.color} />,
    poll: (
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <Reveal blur>
          <p className="mb-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{texts.poll_title || "La communauté vote"}</p>
          <CommunityPoll />
        </Reveal>
      </section>
    ),
    results: (
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
        <Reveal blur>
          <div className="mb-10 flex items-end justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{ui.home_competition_label}</p>
              <h2 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl"><WordsReveal text={texts.matches_title || "Résultats & matchs à venir"} /></h2>
            </div>
            <Link to="/resultats" data-testid="home-all-results-link" className="hidden items-center gap-2 text-sm font-bold uppercase tracking-wider text-orange-400 hover:text-orange-300 sm:flex">
              Tout voir <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {matches.map((m, i) => <Reveal blur key={m.id} delay={i * 0.1}><MatchCard match={m} /></Reveal>)}
        </div>
      </section>
    ),
    manifesto: (
      <section className="border-y border-orange-500/20 bg-[#0B132B]/60">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
          <Reveal blur>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Manifeste</p>
            <h2 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl"><WordsReveal text={ui.home_manifesto_title} /></h2>
          </Reveal>
          <div className="mt-12 grid gap-10 lg:grid-cols-3">
            {chapters.map((c, i) => (
              <Reveal blur key={i} delay={i * 0.15}>
                <Link to={c.link} data-testid={`chapter-${i}`} className="group block border-l-2 border-orange-500/40 pl-6 transition-colors hover:border-orange-500">
                  <p className="text-stroke-orange font-heading text-6xl font-black leading-none transition-all group-hover:text-orange-500">{c.num}</p>
                  <h3 className="mt-4 text-2xl font-bold uppercase tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.text}</p>
                  <p className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-orange-400 transition-transform group-hover:translate-x-1">
                    {c.linkLabel} <ArrowRight className="h-3.5 w-3.5" />
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    ),
    fanwall: <FanWall />,
    newsletter: <NewsletterSection />,
    partners: <PartnerStrip />,
    news: (
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
        <Reveal blur>
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{ui.home_community_label}</p>
            <h2 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl"><WordsReveal text={texts.news_title || "Dernières actualités"} /></h2>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {news.map((n, i) => <Reveal blur key={n.id} delay={i * 0.1}><NewsCard article={n} /></Reveal>)}
        </div>
      </section>
    ),
    banner: (
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8 lg:pb-24">
        <Reveal blur>
          <div className="card-tactical glow-orange relative overflow-hidden p-8 sm:p-12">
            <Swords className="absolute -right-6 -top-6 h-48 w-48 text-orange-500/10" />
            <h2 className="max-w-xl text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
              <WordsReveal text={texts.banner_title || "Portez les couleurs Emisphere"} />
            </h2>
            <p className="mt-4 max-w-xl text-slate-300">
              {texts.banner_text || "Maillot officiel Home 2026 et édition Pro Away en stock limité. Flocage personnalisé avec votre pseudo disponible."}
            </p>
            <Link
              to="/boutique"
              data-testid="home-cta-shop-banner"
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98]"
            >
              {texts.banner_cta || "Voir la boutique"} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>
      </section>
    ),
  };
  const visibleSections = order.filter((k) => sections[k] && !hiddenSections.includes(k));

  return (
    <MotionConfig reducedMotion="user">
    <div data-testid="home-page" className="home-glass">
      <motion.div aria-hidden style={{ scaleX: progress }} data-testid="home-scroll-progress"
        className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-gradient-to-r from-orange-600 via-orange-500 to-amber-300 shadow-[0_0_12px_rgba(255,94,0,0.8)]" />
      <section onMouseMove={moveGlow} className="hero-grid relative flex min-h-[92vh] items-center overflow-hidden">
        <motion.img src={heroImg} alt="" style={{ y: bgY }} className="absolute inset-0 h-[115%] w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060B18]/70 via-[#060B18]/40 to-[#060B18]" />
        <motion.div aria-hidden style={{ x: glowX, y: glowY }}
          className="pointer-events-none absolute left-0 top-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(255,94,0,0.22),transparent_65%)] blur-2xl" />
        {[["12%", "18%"], ["86%", "24%"], ["72%", "78%"], ["20%", "84%"], ["50%", "10%"]].map(([l, t]) => (
          <span key={l + t} aria-hidden style={{ left: l, top: t }}
            className="pointer-events-none absolute font-mono2 text-lg text-orange-500/40">+</span>
        ))}
        <motion.div style={{ opacity: heroFade }} className="relative mx-auto w-full max-w-7xl px-4 py-20 sm:px-8">
          <div className="max-w-3xl">
            <motion.img
              src="/logo.png" alt="Logo Emisphere Esport"
              initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="mb-8 h-24 w-24 object-contain drop-shadow-[0_0_25px_rgba(255,94,0,0.35)]"
            />
            <motion.p
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
              className="mb-4 inline-block rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-orange-400"
            >
              {texts.badge || "Structure Esport Française — League of Legends & TFT"}
            </motion.p>
            <h1 className="text-4xl font-black uppercase leading-none tracking-tight sm:text-6xl lg:text-7xl">
              <MaskedLine index={0}>{texts.title_1 || "Dominer la Faille."}</MaskedLine>
              <MaskedLine index={1} className="text-glow text-orange-500">{texts.title_2 || "Écrire l'Histoire."}</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              {texts.intro || "Rejoignez la communauté Emisphere Esport : suivez nos résultats en direct, découvrez nos joueurs et portez fièrement nos couleurs avec le maillot officiel 2026."}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/boutique"
                data-testid="hero-cta-shop"
                className="glow-orange flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98]"
              >
                <Shirt className="h-5 w-5" /> {texts.cta_primary || "Acheter le maillot officiel"}
              </Link>
              <Link
                to="/rosters"
                data-testid="hero-cta-rosters"
                className="flex items-center gap-2 rounded-xl border border-orange-500/40 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-orange-400 transition-all hover:scale-[1.02] hover:bg-orange-500/10 active:scale-[0.98]"
              >
                <Users className="h-5 w-5" /> {texts.cta_secondary || "Découvrir l'équipe"}
              </Link>
              {discordUrl && (
                <a
                  href={discordUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-cta-discord"
                  className="flex items-center gap-2 rounded-xl bg-[#5865F2] px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-white shadow-[0_0_30px_rgba(88,101,242,0.35)] transition-all hover:scale-[1.02] hover:bg-[#4752C4] active:scale-[0.98]"
                >
                  <DiscordIcon className="h-5 w-5" /> {texts.cta_discord || "Rejoindre la communauté Emisphere"}
                  {discordOnline !== null && (
                    <span data-testid="hero-discord-online"
                      className="ml-1 flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold normal-case tracking-normal">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                      {discordOnline.toLocaleString("fr-FR")} en ligne
                    </span>
                  )}
                </a>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
            >
              <Countdown matches={matches} />
            </motion.div>
            {(stats.length > 0 || followers.length > 0) && (
              <div className="mt-12 flex max-w-2xl flex-wrap gap-4">
                {stats.map((s, i) => (
                  <Reveal blur key={s.id} delay={1 + i * 0.12} y={24} className="min-w-[140px] flex-1">
                    <div data-testid={`stat-box-${s.id}`} className="card-tactical p-4 text-center">
                      <p className="font-heading text-3xl font-black text-orange-500"><CountUp value={s.value} /></p>
                      <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-slate-400">{s.label}</p>
                    </div>
                  </Reveal>
                ))}
                {followers.map((f, i) => (
                  <Reveal blur key={f.provider} delay={1 + (stats.length + i) * 0.12} y={24} className="min-w-[140px] flex-1">
                    <div data-testid={`stat-box-social-${f.provider}`} className="card-tactical p-4 text-center">
                      <p className="flex items-center justify-center gap-2 font-heading text-3xl font-black text-orange-500">
                        {f.provider === "instagram" ? <Instagram className="h-5 w-5" /> : <Twitter className="h-5 w-5" />}
                        <CountUp value={f.followers} />
                      </p>
                      <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Abonnés {f.provider === "instagram" ? "Instagram" : "X"}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Streamers liveOnly className="py-12" />
      </div>

      {visibleSections.map((k, i) => (
        <Fragment key={k}>
          {i > 0 && k !== "marquee" && visibleSections[i - 1] !== "marquee" && <SectionDivider />}
          <div className={i % 2 ? "bg-[#0B132B]/40" : ""}>{sections[k]}</div>
        </Fragment>
      ))}
    </div>
    </MotionConfig>
  );
}
